import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import type { AuthorReplyItem } from '../gen/types'

export function useReviews(params?: { repo?: string; status?: string }) {
  return useQuery({
    queryKey: ['reviews', params] as const,
    queryFn: () => api.listReviews(params ?? {}),
    select: (d) => d.reviews,
  })
}

export function useReview(reviewId: string) {
  return useQuery({
    queryKey: ['review', reviewId] as const,
    queryFn: () => api.getReview(reviewId),
    select: (d) => d.review,
    enabled: Boolean(reviewId),
  })
}

export function useReviewStack(reviewId: string) {
  return useQuery({
    queryKey: ['review', reviewId] as const,
    queryFn: () => api.getReview(reviewId),
    select: (d) => d.stack ?? [],
    enabled: Boolean(reviewId),
  })
}

export function useRevisions(reviewId: string) {
  return useQuery({
    queryKey: ['revisions', reviewId] as const,
    queryFn: () => api.listRevisions(reviewId),
    select: (d) => d.revisions,
    enabled: Boolean(reviewId),
  })
}

export function useRevisionComparison(
  reviewId: string,
  headRevisionId: string | null,
  baseRevisionId: string | null,
) {
  return useQuery({
    queryKey: ['compareRevisions', reviewId, headRevisionId, baseRevisionId] as const,
    queryFn: () => api.compareRevisions(reviewId, headRevisionId!, baseRevisionId ?? ''),
    enabled: Boolean(reviewId && headRevisionId && baseRevisionId !== null),
    staleTime: 5 * 60 * 1000,
  })
}

export function useFindings(reviewId: string) {
  return useQuery({
    queryKey: ['findings', reviewId] as const,
    queryFn: () => api.listFindings(reviewId),
    select: (d) => d.findings,
    enabled: Boolean(reviewId),
  })
}

export function useSignals(reviewId: string) {
  return useQuery({
    queryKey: ['signals', reviewId] as const,
    queryFn: () => api.listSignals(reviewId),
    select: (d) => d.signals,
    enabled: Boolean(reviewId),
  })
}

export function useTimeline(reviewId: string) {
  return useQuery({
    queryKey: ['timeline', reviewId] as const,
    queryFn: () => api.getTimeline(reviewId),
    select: (d) => d.events,
    enabled: Boolean(reviewId),
    refetchInterval: 15_000,
  })
}

export function useReviewers(reviewId: string) {
  return useQuery({
    queryKey: ['reviewers', reviewId] as const,
    queryFn: () => api.listReviewers(reviewId),
    enabled: Boolean(reviewId),
    refetchInterval: 30_000,
  })
}

export function useAssignableReviewers(reviewId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['assignableReviewers', reviewId] as const,
    queryFn: () => api.listAssignableReviewers(reviewId),
    select: (d) => d.candidates,
    enabled: Boolean(reviewId) && enabled,
  })
}

export function useAssignReviewers(reviewId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (githubLogins: string[]) => api.assignReviewers(reviewId, githubLogins),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reviewers', reviewId] })
      void qc.invalidateQueries({ queryKey: ['timeline', reviewId] })
    },
  })
}

export function useUnassignReviewer(reviewId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (assignmentId: string) => api.unassignReviewer(reviewId, assignmentId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reviewers', reviewId] })
      void qc.invalidateQueries({ queryKey: ['timeline', reviewId] })
    },
  })
}

export function useSubmitReviewerDecision(reviewId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ action, comment }: { action: string; comment: string }) =>
      api.submitReviewerDecision(reviewId, action, comment, true),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reviewers', reviewId] })
      void qc.invalidateQueries({ queryKey: ['review', reviewId] })
      void qc.invalidateQueries({ queryKey: ['timeline', reviewId] })
    },
  })
}

export function useHumanDecision(reviewId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ action, comment }: { action: string; comment: string }) =>
      api.humanDecision(reviewId, action, comment),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['review', reviewId] })
      void qc.invalidateQueries({ queryKey: ['timeline', reviewId] })
    },
  })
}

export function useUpdateFinding(reviewId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ findingId, status }: { findingId: string; status: string }) =>
      api.updateFinding(reviewId, findingId, status),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['findings', reviewId] })
    },
  })
}

export function useAuthorReply(reviewId: string) {
  return useMutation({
    mutationFn: (replies: AuthorReplyItem[]) => api.authorReply(reviewId, replies),
  })
}

export function usePostComment(reviewId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ message, findingId, anchor }: {
      message: string
      findingId?: string
      anchor?: { path: string; revisionId: string; range: { startLine: number; endLine: number } }
    }) => api.postComment(reviewId, message, findingId, anchor),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['timeline', reviewId] })
    },
  })
}

export function useDecisionBrief(reviewId: string) {
  return useQuery({
    queryKey: ['decisionBrief', reviewId] as const,
    queryFn: () => api.getDecisionBrief(reviewId),
    select: (d) => d.brief,
    enabled: Boolean(reviewId),
    retry: false,
  })
}
